import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useModal } from '../../context/ModalContext';
import { shouldShowApiToast } from '../../utils/helpers';
import {
  Bot, Pencil, Save, Trash2, AlertTriangle, Download, BarChart3, Clipboard,
  TrendingUp, FileText, Loader2, MessageSquare, Send, HelpCircle, Search,
  Target, BookOpen, Smartphone, Zap
} from 'lucide-react';
import {
  exportToCSV,
  exportToJSON,
  exportToExcel,
  exportToPDF,
  formatForExport,
  getExportFilename
} from '../../utils/exportUtils';
import {
  getChatbotRules,
  getChatbotRule,
  createChatbotRule,
  updateChatbotRule,
  deleteChatbotRule,
  toggleChatbotRule,
  duplicateChatbotRule,
  getChatbotStats,
  getChatbotTopKeywords,
  getChatbotFallback,
  saveChatbotFallback,
  clearApiMessage,
  clearError,
  clearChatbotState,
  selectChatbotRules,
  selectChatbotPagination,
  selectCurrentChatbotRule,
  selectChatbotStats,
  selectChatbotTopKeywords,
  selectChatbotFallback,
  selectChatbotLoading,
  selectChatbotSubmitting,
  selectChatbotApiMessage,
  selectChatbotError,
} from '../../store/slices/growth/chatbotSlice';
import { selectCurrentOrganization } from '../../store/slices/organizationSlice';

const Chatbot = ({ showToast }) => {
  const dispatch = useDispatch();
  const { openModal, closeModal } = useModal();

  // ==================== STATE ====================
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [editingRuleId, setEditingRuleId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // ==================== REFS ====================
  const editKeywordRef = useRef(null);
  const editReplyRef = useRef(null);
  const editActiveRef = useRef(null);
  const fallbackRef = useRef(null);

  // Track last toast message to prevent duplicates
  const lastToastRef = useRef({ message: null, timestamp: 0 });

  // ==================== SELECTORS ====================
  const rules = useSelector(selectChatbotRules) || [];
  const pagination = useSelector(selectChatbotPagination) || {
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0
  };
  const currentRule = useSelector(selectCurrentChatbotRule);
  const stats = useSelector(selectChatbotStats);
  const topKeywords = useSelector(selectChatbotTopKeywords) || [];
  const fallback = useSelector(selectChatbotFallback);
  const loading = useSelector(selectChatbotLoading);
  const isSubmittingRedux = useSelector(selectChatbotSubmitting);
  const apiMessage = useSelector(selectChatbotApiMessage);
  const error = useSelector(selectChatbotError);

  // Organization
  const organization = useSelector(selectCurrentOrganization);
  const organizationName = organization?.name || 'Grace Chapel';

  // ==================== TOAST HELPER ====================
  const showToastMessage = useCallback((message, type = 'info') => {
    if (showToast && typeof showToast === 'function') {
      showToast(message, type);
    }
  }, [showToast]);

  // ==================== EFFECTS ====================

  // Load data on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        await Promise.all([
          dispatch(getChatbotRules({
            per_page: itemsPerPage,
            page: currentPage,
            search: searchTerm,
            status: statusFilter
          })),
          dispatch(getChatbotStats()),
          dispatch(getChatbotTopKeywords({ limit: 5 })),
          dispatch(getChatbotFallback())
        ]);
      } catch (err) {
        console.error('Error loading chatbot data:', err);
      }
    };

    loadData();

    return () => {
      dispatch(clearChatbotState());
    };
  }, [dispatch]);

  // Handle search and filters
  useEffect(() => {
    const timer = setTimeout(() => {
      const params = {
        per_page: itemsPerPage,
        page: currentPage,
        search: searchTerm
      };
      if (statusFilter) params.status = statusFilter;

      dispatch(getChatbotRules(params));
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm, statusFilter, currentPage, itemsPerPage, dispatch]);

  // Display API messages - with deduplication (matching Organization pattern)
  useEffect(() => {
    if (!apiMessage || !showToast || !shouldShowApiToast(apiMessage, !!error)) return;

    const messageKey = `${apiMessage}_${error ? 'error' : 'success'}`;
    const now = Date.now();

    if (lastToastRef.current.message === messageKey &&
      (now - lastToastRef.current.timestamp) < 2000) {
      return;
    }

    lastToastRef.current = { message: messageKey, timestamp: now };

    const toastType = error ? 'error' : 'success';
    showToast(apiMessage, toastType);

    setTimeout(() => {
      dispatch(clearApiMessage());
      dispatch(clearError());
    }, 500);
  }, [apiMessage, error, dispatch, showToast]);

  // ==================== PAGINATION ====================

  const totalItems = pagination?.total || 0;
  const totalPages = pagination?.last_page || 1;
  const currentPageNum = pagination?.current_page || 1;

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToPreviousPage = () => {
    if (currentPageNum > 1) {
      setCurrentPage(currentPageNum - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToNextPage = () => {
    if (currentPageNum < totalPages) {
      setCurrentPage(currentPageNum + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPageNum - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }
    return pageNumbers;
  };

  // ==================== TOGGLE RULE ====================

  const toggleRule = async (id, active) => {
    try {
      const result = await dispatch(toggleChatbotRule(id)).unwrap();
      if (result.success) {
        await dispatch(getChatbotRules({
          per_page: itemsPerPage,
          page: currentPage,
          search: searchTerm,
          status: statusFilter
        }));
        await dispatch(getChatbotStats());
        const rule = rules.find(r => r.id === id);
        showToastMessage(`Rule "${rule?.keyword?.split(',')[0]?.trim() || 'Rule'}" ${active ? 'enabled' : 'disabled'}`, active ? 'success' : 'info');
      }
    } catch (err) {
      showToastMessage(err.message || 'Failed to toggle rule', 'error');
    }
  };

  // ==================== CREATE RULE ====================

  const openAddFaqRule = () => {
    const CreateModalContent = () => {
      const [keyword, setKeyword] = useState('');
      const [reply, setReply] = useState('');
      const [isActive, setIsActive] = useState(true);

      return (
        <div>
          <div className="modal-header">
            <div className="modal-title"><Bot size={16} style={{ verticalAlign: '-3px' }} /> Add FAQ Auto-Reply Rule</div>
            <button className="modal-close" onClick={closeModal}>×</button>
          </div>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Trigger Keywords <span style={{ color: 'var(--red)' }}>*</span></label>
              <input 
                className="form-input" 
                placeholder="SERVICE TIME, WHEN, TIME" 
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
              />
              <div className="form-hint">Separate multiple keywords with commas. The bot matches any of them.</div>
            </div>
            <div className="form-group">
              <label className="form-label">Auto-Reply Message <span style={{ color: 'var(--red)' }}>*</span></label>
              <textarea 
                className="form-textarea" 
                rows="4" 
                placeholder="Our Sunday service starts at 8:30 AM…"
                value={reply}
                onChange={(e) => setReply(e.target.value)}
              />
              <div className="form-hint">Max 320 characters (2 SMS). Use {'{FirstName}'}, {'{ChurchName}'}, {'{PastorName}'} for personalisation.</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px', background: 'var(--cream)', borderRadius: '8px' }}>
              <span style={{ fontSize: '13px', fontWeight: '500' }}>Activate immediately</span>
              <label className="toggle">
                <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
                <span className="toggle-slider"></span>
              </label>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
            <button 
              className="btn btn-gold btn-sm" 
              disabled={isSubmittingRedux || isSubmitting || !keyword || !reply}
              onClick={async () => {
                if (!keyword.trim()) {
                  showToastMessage('Please enter at least one keyword', 'error');
                  return;
                }
                if (!reply.trim()) {
                  showToastMessage('Please enter a reply message', 'error');
                  return;
                }

                setIsSubmitting(true);
                try {
                  const result = await dispatch(createChatbotRule({
                    keyword: keyword.trim(),
                    reply: reply.trim(),
                    is_active: isActive
                  })).unwrap();

                  if (result.success) {
                    closeModal();
                    await Promise.all([
                      dispatch(getChatbotRules({
                        per_page: itemsPerPage,
                        page: currentPage,
                        search: searchTerm,
                        status: statusFilter
                      })),
                      dispatch(getChatbotStats()),
                      dispatch(getChatbotTopKeywords({ limit: 5 }))
                    ]);
                    showToastMessage('🤖 FAQ rule added!', 'success');
                  }
                } catch (err) {
                  showToastMessage(err.message || 'Failed to add rule', 'error');
                } finally {
                  setIsSubmitting(false);
                }
              }}
            >
              {isSubmittingRedux || isSubmitting ? (
                <><Loader2 size={14} style={{ verticalAlign: '-2px', marginRight: '4px', animation: 'spin 1s linear infinite' }} />Adding...</>
              ) : (
                'Add Rule'
              )}
            </button>
          </div>
        </div>
      );
    };

    openModal(<CreateModalContent />);
  };

  // ==================== EDIT RULE ====================

  const handleEditRule = (rule) => {
    setEditingRuleId(rule.id);

    setTimeout(() => {
      if (editKeywordRef.current) editKeywordRef.current.value = rule.keyword || '';
      if (editReplyRef.current) editReplyRef.current.value = rule.reply || '';
      if (editActiveRef.current) editActiveRef.current.checked = rule.is_active !== false;
    }, 50);

    const EditModalContent = () => {
      const [keyword, setKeyword] = useState(rule.keyword || '');
      const [reply, setReply] = useState(rule.reply || '');
      const [isActive, setIsActive] = useState(rule.is_active !== false);

      return (
        <div>
          <div className="modal-header">
            <div className="modal-title"><Pencil size={16} style={{ verticalAlign: '-3px' }} /> Edit FAQ Rule</div>
            <button className="modal-close" onClick={closeModal}>×</button>
          </div>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Trigger Keywords <span style={{ color: 'var(--red)' }}>*</span></label>
              <input 
                className="form-input" 
                ref={editKeywordRef}
                defaultValue={rule.keyword || ''}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="SERVICE TIME, WHEN, TIME"
              />
              <div className="form-hint">Separate multiple keywords with commas.</div>
            </div>
            <div className="form-group">
              <label className="form-label">Auto-Reply Message <span style={{ color: 'var(--red)' }}>*</span></label>
              <textarea 
                className="form-textarea" 
                rows="4"
                ref={editReplyRef}
                defaultValue={rule.reply || ''}
                onChange={(e) => setReply(e.target.value)}
              />
              <div className="form-hint">Use {'{FirstName}'}, {'{ChurchName}'}, {'{PastorName}'} for personalisation</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px', background: 'var(--cream)', borderRadius: '8px' }}>
              <span style={{ fontSize: '13px', fontWeight: '500' }}>Active</span>
              <label className="toggle">
                <input type="checkbox" ref={editActiveRef} defaultChecked={rule.is_active !== false} onChange={(e) => setIsActive(e.target.checked)} />
                <span className="toggle-slider"></span>
              </label>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
            <button 
              className="btn btn-gold btn-sm" 
              disabled={isSubmittingRedux || isSubmitting || !keyword || !reply}
              onClick={async () => {
                if (!keyword.trim()) {
                  showToastMessage('Please enter at least one keyword', 'error');
                  return;
                }
                if (!reply.trim()) {
                  showToastMessage('Please enter a reply message', 'error');
                  return;
                }

                setIsSubmitting(true);
                try {
                  const result = await dispatch(updateChatbotRule({
                    id: rule.id,
                    data: {
                      keyword: keyword.trim(),
                      reply: reply.trim(),
                      is_active: isActive
                    }
                  })).unwrap();

                  if (result.success) {
                    closeModal();
                    await Promise.all([
                      dispatch(getChatbotRules({
                        per_page: itemsPerPage,
                        page: currentPage,
                        search: searchTerm,
                        status: statusFilter
                      })),
                      dispatch(getChatbotStats()),
                      dispatch(getChatbotTopKeywords({ limit: 5 }))
                    ]);
                    showToastMessage('✅ FAQ rule updated!', 'success');
                  }
                } catch (err) {
                  showToastMessage(err.message || 'Failed to update rule', 'error');
                } finally {
                  setIsSubmitting(false);
                }
              }}
            >
              {isSubmittingRedux || isSubmitting ? (
                <><Loader2 size={14} style={{ verticalAlign: '-2px', marginRight: '4px', animation: 'spin 1s linear infinite' }} />Saving...</>
              ) : (
                <><Save size={14} style={{ verticalAlign: '-2px' }} /> Save Changes</>
              )}
            </button>
          </div>
        </div>
      );
    };

    openModal(<EditModalContent />);
  };

  // ==================== DELETE RULE ====================

  const handleDeleteRule = (rule) => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Trash2 size={16} style={{ verticalAlign: '-3px' }} /> Delete FAQ Rule</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '14px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Are you sure you want to delete this FAQ rule?
          </p>
          <div style={{ 
            padding: '12px', 
            background: 'var(--red-bg)', 
            borderRadius: '8px', 
            fontSize: '13px', 
            color: 'var(--red)'
          }}>
            <AlertTriangle size={14} style={{ verticalAlign: '-2px' }} /> This will permanently remove the rule and it will no longer auto-reply.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-danger btn-sm" 
            disabled={isSubmittingRedux || isSubmitting}
            onClick={async () => {
              setIsSubmitting(true);
              try {
                const result = await dispatch(deleteChatbotRule(rule.id)).unwrap();
                if (result.success) {
                  closeModal();
                  await Promise.all([
                    dispatch(getChatbotRules({
                      per_page: itemsPerPage,
                      page: currentPage,
                      search: searchTerm,
                      status: statusFilter
                    })),
                    dispatch(getChatbotStats()),
                    dispatch(getChatbotTopKeywords({ limit: 5 }))
                  ]);
                  showToastMessage('🗑️ FAQ rule deleted', 'error');
                }
              } catch (err) {
                showToastMessage(err.message || 'Failed to delete rule', 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            {isSubmittingRedux || isSubmitting ? (
              <><Loader2 size={14} style={{ verticalAlign: '-2px', marginRight: '4px', animation: 'spin 1s linear infinite' }} />Deleting...</>
            ) : (
              'Delete Rule'
            )}
          </button>
        </div>
      </div>
    );
  };

  // ==================== DUPLICATE RULE ====================

  const handleDuplicateRule = async (rule) => {
    setIsSubmitting(true);
    try {
      const result = await dispatch(duplicateChatbotRule(rule.id)).unwrap();
      if (result.success) {
        await Promise.all([
          dispatch(getChatbotRules({
            per_page: itemsPerPage,
            page: currentPage,
            search: searchTerm,
            status: statusFilter
          })),
          dispatch(getChatbotStats()),
          dispatch(getChatbotTopKeywords({ limit: 5 }))
        ]);
        showToastMessage('📋 FAQ rule duplicated!', 'success');
      }
    } catch (err) {
      showToastMessage(err.message || 'Failed to duplicate rule', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ==================== SAVE FALLBACK ====================

  const handleSaveFallback = async () => {
    const message = fallbackRef.current?.value?.trim() || '';
    if (!message) {
      showToastMessage('Please enter a fallback reply message', 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await dispatch(saveChatbotFallback({
        message: message,
        organization_id: organization?.id
      })).unwrap();

      if (result.success) {
        showToastMessage('💾 Fallback reply saved!', 'success');
        await dispatch(getChatbotFallback());
      }
    } catch (err) {
      showToastMessage(err.message || 'Failed to save fallback reply', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ==================== EXPORT FUNCTIONS ====================

  const openExportModal = () => {
    const data = rules.map(r => ({
      'Keywords': r.keyword,
      'Auto-Reply': r.reply,
      'Hits': r.hits || 0,
      'Status': r.is_active ? 'Active' : 'Inactive',
      'Last Used': r.last_used_at ? new Date(r.last_used_at).toLocaleDateString() : 'Never'
    }));

    const fields = [
      { key: 'Keywords', label: 'Keywords' },
      { key: 'Auto-Reply', label: 'Auto-Reply' },
      { key: 'Hits', label: 'Hits' },
      { key: 'Status', label: 'Status' },
      { key: 'Last Used', label: 'Last Used' }
    ];

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Download size={16} style={{ verticalAlign: '-3px' }} /> Export FAQ Rules</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ fontSize: '13px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Export {data.length} FAQ rule{data.length !== 1 ? 's' : ''} in your preferred format.
          </div>
          
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-mid)', marginBottom: '8px' }}>
              Select Format
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
              {['CSV', 'JSON', 'Excel', 'PDF'].map(format => (
                <button
                  key={format}
                  className="btn btn-ghost btn-sm"
                  disabled={isExporting}
                  onClick={() => {
                    setIsExporting(true);
                    const formattedData = formatForExport(data, fields);
                    const filename = getExportFilename('faq_rules');
                    
                    try {
                      switch (format) {
                        case 'CSV':
                          exportToCSV(formattedData, filename);
                          break;
                        case 'JSON':
                          exportToJSON(formattedData, filename);
                          break;
                        case 'Excel':
                          exportToExcel(formattedData, filename);
                          break;
                        case 'PDF':
                          exportToPDF(formattedData, 'FAQ Rules Report', filename);
                          break;
                        default:
                          showToastMessage('Export format not supported', 'error');
                          setIsExporting(false);
                          return;
                      }
                      showToastMessage(`📊 ${data.length} rules exported as ${format}!`, 'success');
                      closeModal();
                    } catch (err) {
                      showToastMessage('Failed to export', 'error');
                    } finally {
                      setIsExporting(false);
                    }
                  }}
                  style={{ 
                    padding: '8px', 
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px',
                    height: 'auto',
                    border: '2px solid var(--border)',
                    borderRadius: '8px',
                    transition: 'all 0.2s',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'var(--gold)';
                    e.currentTarget.style.background = 'var(--gold-pale)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'var(--border)';
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  {isExporting ? (
                    <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
                  ) : (
                    <span style={{ fontSize: '20px', display: 'inline-flex' }}>
                      {format === 'CSV' && <BarChart3 size={20} />}
                      {format === 'JSON' && <Clipboard size={20} />}
                      {format === 'Excel' && <TrendingUp size={20} />}
                      {format === 'PDF' && <FileText size={20} />}
                    </span>
                  )}
                  <span style={{ fontSize: '11px' }}>{format}</span>
                </button>
              ))}
            </div>
          </div>

          <div style={{
            padding: '10px 12px',
            background: 'var(--cream)',
            borderRadius: '8px',
            fontSize: '12px',
            color: 'var(--text-light)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span><BarChart3 size={14} style={{ verticalAlign: '-2px' }} /> {data.length} rules will be exported</span>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal} disabled={isExporting}>Cancel</button>
        </div>
      </div>
    );
  };

  // ==================== RENDER ====================

  const statsData = stats || {
    active_rules: 0,
    total_rules: 0,
    total_hits: 0,
    total_conversations: 0,
    fallback_conversations: 0
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ fontSize: '14px', color: 'var(--text-mid)' }}>
          <Bot size={16} style={{ verticalAlign: '-3px' }} /> Auto-reply to common member questions via SMS — no staff needed.
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button className="btn btn-gold btn-sm" onClick={openAddFaqRule} disabled={loading || isSubmittingRedux} title="Add new FAQ rule">
            + Add FAQ Rule
          </button>
          <button className="btn btn-ghost btn-sm" onClick={openExportModal} disabled={loading || rules.length === 0 || isExporting} title="Export all rules">
            {isExporting ? (
              <><Loader2 size={14} style={{ verticalAlign: '-2px', marginRight: '4px', animation: 'spin 1s linear infinite' }} />Exporting...</>
            ) : (
              <><Download size={16} style={{ verticalAlign: '-3px' }} /> Export</>
            )}
          </button>
        </div>
      </div>

      <div className="stats-grid" style={{ marginBottom: '20px' }}>
        <div className="stat-card gold" style={{ cursor: 'default' }}>
          <div className="stat-label">Active Rules</div>
          <div className="stat-value">{statsData.active_rules || 0}</div>
          <div className="stat-change up">running now</div>
          <div className="stat-icon"><Bot size={24} /></div>
        </div>
        <div className="stat-card green" style={{ cursor: 'default' }}>
          <div className="stat-label">Auto-Replies Sent</div>
          <div className="stat-value">{statsData.total_hits?.toLocaleString() || 0}</div>
          <div className="stat-change up">total responses</div>
          <div className="stat-icon"><Send size={24} /></div>
        </div>
        <div className="stat-card blue" style={{ cursor: 'default' }}>
          <div className="stat-label">Total Conversations</div>
          <div className="stat-value">{statsData.total_conversations || 0}</div>
          <div className="stat-change up">all time</div>
          <div className="stat-icon"><MessageSquare size={24} /></div>
        </div>
        <div className="stat-card purple" style={{ cursor: 'default' }}>
          <div className="stat-label">Top Question</div>
          <div className="stat-value">{topKeywords[0]?.first_keyword || 'N/A'}</div>
          <div className="stat-change up">{topKeywords[0]?.hits || 0} hits</div>
          <div className="stat-icon"><HelpCircle size={24} /></div>
        </div>
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
          <div className="search-box" style={{ maxWidth: '300px', flex: '1' }}>
            <span className="search-icon"><Search size={14} /></span>
            <input 
              type="text" 
              placeholder="Search rules..." 
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              title="Search by keyword or reply"
              disabled={loading}
            />
          </div>
          <select 
            className="form-select" 
            style={{ width: '140px' }}
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
            disabled={loading}
            title="Filter by status"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>
            {rules.length} rules found
          </div>
        </div>
      </div>

      {/* Main Content with Loading State - matching Organization pattern */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Loader2 size={40} style={{ animation: 'spin 1s linear infinite', color: 'var(--text-light)' }} />
          <div style={{ color: 'var(--text-light)', marginTop: '8px' }}>Loading chatbot rules...</div>
        </div>
      ) : (
        <div className="grid-65-35">
          <div>
            <div className="card">
              <div className="card-header">
                <div className="card-title"><Clipboard size={16} style={{ verticalAlign: '-3px' }} /> FAQ Auto-Reply Rules</div>
                <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>Members text any keyword → instant auto-reply</div>
              </div>
              {rules.length > 0 ? (
                rules.map(r => (
                  <div key={r.id} className="automation-rule" style={{ opacity: r.is_active !== false ? 1 : 0.6 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ marginBottom: '6px' }}>
                        {r.keyword?.split(',').map((k, idx) => (
                          <span key={idx} style={{ display: 'inline-flex', fontFamily: 'JetBrains Mono, monospace', fontSize: '11px', fontWeight: '700', padding: '2px 8px', background: 'var(--navy)', color: 'var(--gold-light)', borderRadius: '4px', marginRight: '4px', marginBottom: '4px' }}>
                            {k.trim()}
                          </span>
                        ))}
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--text-mid)', lineHeight: '1.6', background: 'var(--cream)', padding: '10px', borderRadius: '8px' }}>
                        {r.reply}
                      </div>
                      {r.hits > 0 && (
                        <div style={{ fontSize: '11px', color: 'var(--text-light)', marginTop: '4px' }}>
                          <Target size={12} style={{ verticalAlign: '-2px' }} /> {r.hits} hits · {r.last_used_at ? `Last used: ${new Date(r.last_used_at).toLocaleDateString()}` : 'Never used'}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px', flexShrink: 0 }}>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <label className="toggle" title={r.is_active ? 'Click to disable' : 'Click to enable'}>
                          <input type="checkbox" checked={r.is_active !== false} onChange={(e) => toggleRule(r.id, e.target.checked)} disabled={loading} />
                          <span className="toggle-slider"></span>
                        </label>
                        <button 
                          className="btn btn-ghost btn-sm btn-icon" 
                          onClick={() => handleEditRule(r)}
                          disabled={loading || isSubmittingRedux}
                          title="Edit rule"
                        >
                          <Pencil size={16} />
                        </button>
                        <button
                          className="btn btn-ghost btn-sm btn-icon"
                          onClick={() => handleDuplicateRule(r)}
                          disabled={loading || isSubmittingRedux}
                          title="Duplicate rule"
                        >
                          {isSubmittingRedux ? (
                            <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                          ) : (
                            <Clipboard size={16} />
                          )}
                        </button>
                        <button
                          className="btn btn-ghost btn-sm btn-icon"
                          onClick={() => handleDeleteRule(r)}
                          disabled={loading || isSubmittingRedux}
                          title="Delete rule"
                          style={{ color: 'var(--red)' }}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--text-light)', textAlign: 'right' }}>
                        {r.hits || 0} hits
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="empty-state">
                  <div className="empty-icon"><Bot size={48} strokeWidth={1.5} /></div>
                  <div className="empty-title">No rules found</div>
                  <div className="empty-text">Try adjusting your search or filters</div>
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: '16px',
                  paddingTop: '14px',
                  borderTop: '1px solid var(--border)',
                  marginTop: '4px'
                }}>
                  <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>
                    Showing <strong>{((currentPageNum - 1) * itemsPerPage) + 1}</strong> to <strong>{Math.min(currentPageNum * itemsPerPage, totalItems)}</strong> of <strong>{totalItems}</strong> rules
                  </div>
                  
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <label style={{ fontSize: '12px', color: 'var(--text-light)' }}>Show:</label>
                      <select 
                        className="form-select" 
                        style={{ width: 'auto', padding: '4px 8px', fontSize: '12px' }}
                        value={itemsPerPage}
                        onChange={(e) => {
                          setItemsPerPage(Number(e.target.value));
                          setCurrentPage(1);
                        }}
                        disabled={loading}
                        title="Items per page"
                      >
                        <option value={3}>3</option>
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                      </select>
                    </div>
                    
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <button 
                        className="btn btn-ghost btn-sm"
                        onClick={goToPreviousPage}
                        disabled={currentPageNum === 1 || loading}
                        style={{ 
                          opacity: currentPageNum === 1 ? 0.5 : 1, 
                          cursor: currentPageNum === 1 ? 'not-allowed' : 'pointer' 
                        }}
                        title="Previous page"
                      >
                        ← Prev
                      </button>
                      
                      {getPageNumbers().map(page => (
                        <button 
                          key={page}
                          className={`btn btn-sm ${page === currentPageNum ? 'btn-primary' : 'btn-ghost'}`}
                          onClick={() => goToPage(page)}
                          disabled={loading}
                          title={`Go to page ${page}`}
                        >
                          {page}
                        </button>
                      ))}
                      
                      <button 
                        className="btn btn-ghost btn-sm"
                        onClick={goToNextPage}
                        disabled={currentPageNum === totalPages || loading}
                        style={{ 
                          opacity: currentPageNum === totalPages ? 0.5 : 1, 
                          cursor: currentPageNum === totalPages ? 'not-allowed' : 'pointer' 
                        }}
                        title="Next page"
                      >
                        Next →
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div>
            <div className="card" style={{ marginBottom: '16px' }}>
              <div className="card-title" style={{ marginBottom: '12px' }}><BookOpen size={16} style={{ verticalAlign: '-3px' }} /> How It Works</div>
              {[
                { step: '1', text: 'Member texts any keyword to your shortcode 40404', icon: Smartphone },
                { step: '2', text: 'FaithConnect scans the message for matching keywords', icon: Search },
                { step: '3', text: 'Matching auto-reply is sent within seconds', icon: Zap },
                { step: '4', text: 'If no match, message lands in your SMS Inbox', icon: MessageSquare }
              ].map(s => (
                <div key={s.step} style={{ display: 'flex', gap: '10px', padding: '10px 0', borderBottom: '1px solid var(--border-light)' }}>
                  <div style={{ width: '26px', height: '26px', borderRadius: '50%', background: 'var(--navy)', color: 'var(--gold-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: '700', flexShrink: 0 }}>
                    {s.step}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: 'var(--text-mid)' }}>
                    <s.icon size={16} /> {s.text}
                  </div>
                </div>
              ))}
            </div>

            <div className="card" style={{ marginBottom: '16px' }}>
              <div className="card-title" style={{ marginBottom: '12px' }}><MessageSquare size={16} style={{ verticalAlign: '-3px' }} /> Default Fallback Reply</div>
              <textarea 
                className="form-textarea" 
                rows="3" 
                ref={fallbackRef}
                defaultValue={fallback?.message || "Thank you for texting Grace Chapel! 🙏 Our team will get back to you shortly. For urgent matters, call +233244000001. Office hours: Mon-Fri, 9 AM - 5 PM."} 
                disabled={loading}
              />
              <div className="form-hint">This reply is sent when no keyword matches</div>
              <button 
                className="btn btn-gold btn-sm" 
                style={{ marginTop: '10px', width: '100%' }} 
                onClick={handleSaveFallback}
                disabled={loading || isSubmittingRedux}
                title="Save fallback reply"
              >
                {isSubmittingRedux || isSubmitting ? (
                  <><Loader2 size={14} style={{ verticalAlign: '-2px', marginRight: '4px', animation: 'spin 1s linear infinite' }} />Saving...</>
                ) : (
                  <><Save size={14} style={{ verticalAlign: '-2px' }} /> Save Fallback</>
                )}
              </button>
            </div>

            <div className="card">
              <div className="card-title" style={{ marginBottom: '12px' }}><BarChart3 size={16} style={{ verticalAlign: '-3px' }} /> Top Keywords This Month</div>
              {topKeywords.length > 0 ? (
                topKeywords.map((item, index) => {
                  const maxHits = topKeywords[0]?.hits || 1;
                  const percentage = Math.min((item.hits / maxHits) * 100, 100);
                  const colors = ['#4f46e5', '#059669', '#d97706', '#7c3aed', '#0891b2'];
                  const color = colors[index % colors.length];
                  
                  return (
                    <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0', borderBottom: '1px solid var(--border-light)' }}>
                      <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '12px', fontWeight: '600', color: 'var(--navy)', minWidth: '100px' }}>
                        {item.first_keyword || item.keyword}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div className="progress-bar" style={{ height: '6px' }}>
                          <div className="progress-fill" style={{ width: `${percentage}%`, background: color }}></div>
                        </div>
                      </div>
                      <div style={{ fontSize: '12px', fontWeight: '600', fontFamily: 'JetBrains Mono, monospace', color: 'var(--text-mid)', minWidth: '28px', textAlign: 'right' }}>
                        {item.hits}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-light)', fontSize: '13px' }}>
                  No keyword data yet
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @media (max-width: 1024px) {
          .grid-65-35 {
            grid-template-columns: 1fr !important;
          }
          .stats-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
        @media (max-width: 768px) {
          .stats-grid {
            grid-template-columns: 1fr !important;
          }
          .card-header {
            flex-direction: column;
            align-items: flex-start !important;
            gap: 10px;
          }
          .pagination-controls {
            flex-direction: column;
            align-items: center;
          }
          .page-numbers {
            flex-wrap: wrap;
            justify-content: center;
          }
          .automation-rule {
            flex-wrap: wrap;
          }
        }
        @media (max-width: 480px) {
          .btn-sm {
            padding: 4px 8px;
            font-size: 11px;
          }
        }
        .btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .automation-rule {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 0;
          border-bottom: 1px solid var(--border-light);
          gap: 12px;
        }
        .automation-rule:last-child {
          border-bottom: none;
        }
        .grid-65-35 {
          display: grid;
          grid-template-columns: 65% 35%;
          gap: 16px;
        }
        .badge {
          padding: 3px 10px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 500;
          display: inline-block;
        }
        .badge-green {
          background: var(--green-bg);
          color: var(--green);
        }
        .badge-red {
          background: var(--red-bg);
          color: var(--red);
        }
        .badge-amber {
          background: var(--amber-bg);
          color: var(--amber);
        }
        .progress-bar {
          width: 100%;
          height: 6px;
          background: var(--cream);
          border-radius: 4px;
          overflow: hidden;
        }
        .progress-fill {
          height: 100%;
          border-radius: 4px;
          transition: width 0.5s ease;
        }
        .toggle {
          position: relative;
          display: inline-block;
          width: 40px;
          height: 22px;
          flex-shrink: 0;
        }
        .toggle input {
          opacity: 0;
          width: 0;
          height: 0;
        }
        .toggle-slider {
          position: absolute;
          cursor: pointer;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: #ccc;
          transition: 0.3s;
          border-radius: 22px;
        }
        .toggle-slider:before {
          position: absolute;
          content: "";
          height: 16px;
          width: 16px;
          left: 3px;
          bottom: 3px;
          background: white;
          transition: 0.3s;
          border-radius: 50%;
        }
        .toggle input:checked + .toggle-slider {
          background: var(--gold);
        }
        .toggle input:checked + .toggle-slider:before {
          transform: translateX(18px);
        }
        .btn-primary {
          background: var(--gold);
          color: var(--navy);
          border: none;
        }
        .btn-primary:hover {
          background: var(--gold-dark);
        }
        .btn-sm {
          padding: 4px 10px;
          font-size: 12px;
        }
        .btn-icon {
          padding: 4px 6px;
        }
        .stat-change {
          font-size: 12px;
          font-weight: 600;
          margin-top: 2px;
        }
        .stat-change.up {
          color: var(--green);
        }
        .stat-change.down {
          color: var(--red);
        }
        .form-hint {
          font-size: 11px;
          color: var(--text-light);
          margin-top: 4px;
        }
        .empty-state {
          text-align: center;
          padding: 40px 20px;
        }
        .empty-icon {
          font-size: 48px;
          margin-bottom: 12px;
        }
        .empty-title {
          font-size: 16px;
          font-weight: 600;
          color: var(--navy);
          margin-bottom: 4px;
        }
        .empty-text {
          font-size: 13px;
          color: var(--text-light);
        }
        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }
        .card-title {
          font-size: 15px;
          font-weight: 700;
          color: var(--navy);
        }
        .stat-card {
          padding: 16px;
          border-radius: 12px;
          background: var(--white);
          border: 1px solid var(--border);
          position: relative;
          overflow: hidden;
          transition: all 0.2s;
        }
        .stat-card:hover {
          transform: translateY(-2px);
          box-shadow: var(--shadow);
        }
        .stat-label {
          font-size: 12px;
          font-weight: 500;
          color: var(--text-light);
          margin-bottom: 4px;
        }
        .stat-value {
          font-size: 28px;
          font-weight: 700;
          font-family: 'Playfair Display', serif;
        }
        .stat-change {
          font-size: 12px;
          font-weight: 600;
          margin-top: 2px;
        }
        .stat-change.up {
          color: var(--green);
        }
        .stat-change.down {
          color: var(--red);
        }
        .stat-icon {
          position: absolute;
          right: 16px;
          top: 16px;
          font-size: 24px;
          opacity: 0.3;
        }
        .stat-card.gold .stat-value {
          color: var(--gold);
        }
        .stat-card.green .stat-value {
          color: var(--green);
        }
        .stat-card.blue .stat-value {
          color: var(--blue);
        }
        .stat-card.purple .stat-value {
          color: var(--purple);
        }
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 16px;
        }
        .search-box {
          display: flex;
          align-items: center;
          background: var(--white);
          border: 1px solid var(--border);
          border-radius: 8px;
          padding: 4px 12px;
          transition: all 0.2s;
        }
        .search-box:focus-within {
          border-color: var(--gold);
          box-shadow: 0 0 0 3px var(--gold-pale);
        }
        .search-icon {
          font-size: 14px;
          color: var(--text-light);
          margin-right: 8px;
        }
        .search-box input {
          border: none;
          outline: none;
          padding: 8px 0;
          flex: 1;
          font-size: 13px;
          background: transparent;
        }
        .form-select {
          padding: 8px 12px;
          border: 1px solid var(--border);
          border-radius: 8px;
          font-size: 13px;
          background: var(--white);
          color: var(--text);
          outline: none;
          transition: all 0.2s;
        }
        .form-select:focus {
          border-color: var(--gold);
          box-shadow: 0 0 0 3px var(--gold-pale);
        }
        .form-input {
          width: 100%;
          padding: 8px 12px;
          border: 1px solid var(--border);
          border-radius: 8px;
          font-size: 13px;
          background: var(--white);
          color: var(--text);
          outline: none;
          transition: all 0.2s;
        }
        .form-input:focus {
          border-color: var(--gold);
          box-shadow: 0 0 0 3px var(--gold-pale);
        }
        .form-textarea {
          width: 100%;
          padding: 8px 12px;
          border: 1px solid var(--border);
          border-radius: 8px;
          font-size: 13px;
          background: var(--white);
          color: var(--text);
          outline: none;
          resize: vertical;
          font-family: inherit;
          transition: all 0.2s;
        }
        .form-textarea:focus {
          border-color: var(--gold);
          box-shadow: 0 0 0 3px var(--gold-pale);
        }
        .form-group {
          margin-bottom: 16px;
        }
        .form-label {
          display: block;
          font-size: 13px;
          font-weight: 600;
          color: var(--navy);
          margin-bottom: 4px;
        }
        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
        }
        @media (max-width: 480px) {
          .form-row {
            grid-template-columns: 1fr;
          }
        }
        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 24px;
          border-bottom: 1px solid var(--border);
        }
        .modal-title {
          font-size: 18px;
          font-weight: 700;
          color: var(--navy);
        }
        .modal-close {
          background: none;
          border: none;
          font-size: 24px;
          color: var(--text-light);
          cursor: pointer;
          padding: 0 8px;
        }
        .modal-close:hover {
          color: var(--red);
        }
        .modal-body {
          padding: 16px 24px;
          max-height: 70vh;
          overflow-y: auto;
        }
        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 8px;
          padding: 12px 24px;
          border-top: 1px solid var(--border);
        }
        .btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 16px;
          border: 1px solid transparent;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          text-decoration: none;
          font-family: inherit;
        }
        .btn-ghost {
          background: transparent;
          color: var(--text-mid);
          border-color: var(--border);
        }
        .btn-ghost:hover {
          background: var(--cream);
        }
        .btn-gold {
          background: var(--gold);
          color: var(--navy);
          border-color: var(--gold);
        }
        .btn-gold:hover {
          background: var(--gold-dark);
        }
        .btn-danger {
          background: var(--red);
          color: white;
          border-color: var(--red);
        }
        .btn-danger:hover {
          background: var(--red-dark);
        }
        .btn-success {
          background: var(--green);
          color: white;
          border-color: var(--green);
        }
        .btn-success:hover {
          background: var(--green-dark);
        }
        .btn-sm {
          padding: 4px 10px;
          font-size: 12px;
        }
        .btn-sm.btn-icon {
          padding: 4px 6px;
        }
        .btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .card {
          background: var(--white);
          border-radius: var(--radius);
          border: 1px solid var(--border);
          padding: 16px;
          box-shadow: var(--shadow);
        }
        .card:last-child {
          margin-bottom: 0;
        }
      `}</style>
    </>
  );
};

export default Chatbot;